// Header.jsx — JALE branded top nav.
// Off-black ink background with white nav links and brand-yellow accents.
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FiBriefcase, FiMessageSquare } from "react-icons/fi";
import { useConversations } from "../hooks/useConversations";
import { getAdminRole } from "../hooks/useAdminClaim";
import Logo from "./Logo";
import Confetti from "./Confetti";

// Admin door: 5 logo taps within this window. Works with a thumb on mobile,
// unlike a keyboard code.
const DOOR_TAPS = 5;
const DOOR_WINDOW_MS = 1500;

export default function Header() {
  const [pageState, setPageState] = useState("Sign in");
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const { totalUnread, uid: convUid } = useConversations();
  const tapTimes = useRef([]);
  const [confetti, setConfetti] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setPageState(user ? "Profile" : "Sign in");
    });
    return () => unsub();
  }, [auth]);

  const isActive = (route) => location.pathname === route;

  // Every tap still goes home, so for anyone without the admin claim this is
  // indistinguishable from a normal logo click — no redirect, no error.
  async function onLogoTap() {
    navigate("/");
    const now = Date.now();
    tapTimes.current = tapTimes.current
      .filter((t) => now - t < DOOR_WINDOW_MS)
      .concat(now);
    if (tapTimes.current.length < DOOR_TAPS || !auth.currentUser) return;
    tapTimes.current = [];
    // Force-refresh so a freshly granted claim works without re-signing in.
    if (await getAdminRole(true)) {
      setConfetti(now);
      navigate("/yard-office");
    }
  }

  const linkBase =
    "relative cursor-pointer px-3 py-2 text-sm md:text-base font-semibold rounded-xl " +
    "text-white/80 hover:text-white transition " +
    "hover:bg-white/10 border border-transparent";
  const linkActive =
    "text-white " +
    "after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 " +
    "after:-bottom-1 after:h-[3px] after:w-7 after:rounded-full after:bg-brand-yellow";

  // The portfolio (both variants) is a separate experience with its own nav.
  if (/^\/(portfolio|adams-projects)(\/|$)/.test(location.pathname)) return null;

  return (
    <div className="sticky top-0 z-40">
      {/* Hazard-stripe top accent — pure brand culture */}
      <div
        aria-hidden
        className="h-[3px] w-full"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, #F5C518 0 14px, #0E1116 14px 28px)",
        }}
      />
      <header
        className={
          "bg-brand-ink border-b border-white/10 " +
          "shadow-[0_4px_20px_rgba(0,0,0,0.45)]"
        }
      >
        <div className="mx-auto max-w-6xl px-3 md:px-4 flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <button
            onClick={onLogoTap}
            className="flex-shrink-0 transition-transform duration-200 hover:scale-[1.03] cursor-pointer"
            aria-label="JALE home"
          >
            <Logo size="md" />
          </button>

          {/* Nav */}
          <nav className="hidden md:flex items-center gap-2">
            <button
              className={`${linkBase} ${isActive("/") ? linkActive : ""}`}
              onClick={() => navigate("/")}
            >
              Home
            </button>

            <button
              className={`${linkBase} ${isActive("/jobs") ? linkActive : ""}`}
              onClick={() => navigate("/jobs")}
            >
              Jobs
            </button>

            {convUid && (
              <button
                className={`${linkBase} ${isActive("/messages") ? linkActive : ""} relative`}
                onClick={() => navigate("/messages")}
                aria-label="Messages"
              >
                <FiMessageSquare className="h-4 w-4" />
                {totalUnread > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-brand-yellow text-brand-ink text-[9px] font-bold leading-none">
                    {totalUnread > 9 ? "9+" : totalUnread}
                  </span>
                )}
              </button>
            )}

            <button
              className={`${linkBase} ${
                isActive("/sign-in") || isActive("/profile") ? linkActive : ""
              }`}
              onClick={() => navigate("/profile")}
            >
              {pageState}
            </button>
          </nav>

          {/* Mobile nav */}
          <nav className="md:hidden flex items-center gap-1">
            <button
              className={`${linkBase} ${isActive("/") ? linkActive : ""}`}
              onClick={() => navigate("/")}
              aria-label="Home"
            >
              Home
            </button>

            <button
              className={`${linkBase} ${isActive("/jobs") ? linkActive : ""}`}
              onClick={() => navigate("/jobs")}
              aria-label="Jobs"
            >
              <FiBriefcase className="h-4 w-4" />
            </button>

            {convUid && (
              <button
                className={`${linkBase} ${isActive("/messages") ? linkActive : ""} relative`}
                onClick={() => navigate("/messages")}
                aria-label="Messages"
              >
                <FiMessageSquare className="h-4 w-4" />
                {totalUnread > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-brand-yellow text-brand-ink text-[9px] font-bold leading-none">
                    {totalUnread > 9 ? "9+" : totalUnread}
                  </span>
                )}
              </button>
            )}

            <button
              className={`${linkBase} ${
                isActive("/sign-in") || isActive("/profile") ? linkActive : ""
              }`}
              onClick={() => navigate("/profile")}
              aria-label={pageState}
            >
              {pageState}
            </button>
          </nav>
        </div>
      </header>
      <Confetti trigger={confetti} />
    </div>
  );
}
